import { useEffect, useState } from "react";
import { BriefcaseBusiness, Award, BookOpenCheck, IndianRupee, Clock3, Layers, GraduationCap, Building2, MessageCircle, Phone, Search } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BRAND, WA_LINK } from "../lib/constants";
import { FAQ_SECTIONS } from "../lib/faq";

const ICONS = { BriefcaseBusiness, Award, BookOpenCheck, IndianRupee, Clock3, Layers, GraduationCap, Building2 };

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_SECTIONS.flatMap((s) =>
    s.items.map((i) => ({ "@type": "Question", name: i.q, acceptedAnswer: { "@type": "Answer", text: i.a } }))
  ),
};

const useFaqSchema = () => {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-schema";
    el.text = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(el);
    const prevTitle = document.title;
    document.title = "FAQ — Fees, Placement, Timings & Certificates | TASSEI.in Sangam Vihar";
    return () => { el.remove(); document.title = prevTitle; };
  }, []);
};

export default function FAQ() {
  useFaqSchema();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const sections = FAQ_SECTIONS.map((s) => ({
    ...s,
    items: q ? s.items.filter((i) => `${i.q} ${i.a}`.toLowerCase().includes(q)) : s.items,
  })).filter((s) => s.items.length);
  const total = FAQ_SECTIONS.reduce((n, s) => n + s.items.length, 0);

  return (
    <div data-testid="faq-page">
      <PageHero
        eyebrow="Frequently Asked Questions"
        title="Everything you want to know before joining."
        desc={`${total} honest answers about placements, fees, timings, certificates and life at TASSEI.`}
        image="https://images.pexels.com/photos/5428836/pexels-photo-5428836.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="Students asking questions in class"
      />

      <section className="pt-14 pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 text-[#64748B] absolute left-4 top-1/2 -translate-y-1/2" />
              <Input data-testid="faq-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search a question…" className="h-12 pl-11 rounded-full" />
            </div>
            <nav className="hidden lg:block rounded-3xl border border-slate-200 bg-white p-3" data-testid="faq-section-nav">
              {FAQ_SECTIONS.map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <a key={s.id} href={`#${s.id}`} data-testid={`faq-nav-${s.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-semibold text-[#0A192F] hover:bg-[#00B37E]/8 transition-colors">
                    <Icon className="w-4 h-4 text-[#00B37E]" /> {s.title}
                    <span className="ml-auto text-xs text-[#64748B]">{s.items.length}</span>
                  </a>
                );
              })}
            </nav>
            <div className="rounded-3xl bg-[#0A192F] text-white p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#7CF0BD]">Still have a question?</div>
              <p className="mt-2 text-sm text-white/80">Message us on WhatsApp or call — we reply within a few hours.</p>
              <div className="mt-4 flex flex-col gap-2">
                <a href={WA_LINK("Hi TASSEI, I have a question about your courses")} target="_blank" rel="noopener noreferrer" data-testid="faq-whatsapp-cta">
                  <Button className="w-full bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-11"><MessageCircle className="w-4 h-4 mr-2" /> Ask on WhatsApp</Button>
                </a>
                <a href={`tel:${BRAND.phoneRaw}`} data-testid="faq-call-cta">
                  <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white hover:text-[#0A192F] bg-transparent font-semibold rounded-full h-11"><Phone className="w-4 h-4 mr-2" /> {BRAND.phone}</Button>
                </a>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-10">
            {sections.length === 0 && (
              <div data-testid="faq-empty" className="rounded-3xl border border-dashed border-slate-300 p-10 text-center text-[#64748B]">No questions match "{query}". Try another word or ask us on WhatsApp.</div>
            )}
            {sections.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={s.id} id={s.id} data-testid={`faq-section-${s.id}`} className="scroll-mt-28">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#0A192F] flex items-center justify-center"><Icon className="w-5 h-5 text-[#00B37E]" /></div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-[#0A192F]">{s.title}</h2>
                  </div>
                  <Accordion type="single" collapsible className="rounded-3xl border border-slate-200 bg-white px-6 divide-y-0">
                    {s.items.map((item, i) => (
                      <AccordionItem key={item.q} value={`${s.id}-${i}`} data-testid={`faq-item-${s.id}-${i}`} className="border-slate-100">
                        <AccordionTrigger data-testid={`faq-trigger-${s.id}-${i}`} className="text-left font-heading font-semibold text-[#0A192F] hover:no-underline hover:text-[#00B37E] py-5 text-base">{item.q}</AccordionTrigger>
                        <AccordionContent data-testid={`faq-answer-${s.id}-${i}`} className="text-[#475569] text-base leading-relaxed pb-5">{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
